import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import moment from 'moment';
import config from '../../../../../config';
import TableBuilder from '../../../../../shared/components/table/TableBuilder';
import { getExportPrefix } from '../../../../../shared/components/table/functions';
import Notify from '../../../../../shared/components/widgets/Notify';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';

export default function DspAudienceTargets() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const [forceRefresh, setForceRefresh] = useState(moment());
  const [agGrid, setAgGrid] = useState(null);
  const [updatedRowCount, setUpdatedRowCount] = useState(0);
  const [originalData, setOriginalData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // function jsonCellRenderer(params) {
  // // Hold  cellRenderer: (params) => <pre style={{ fontFamily: 'monospace', margin: 0, padding: 0, whiteSpace: 'pre-wrap' }}>${JSON.stringify(JSON.parse(params.value), null, 2)}</pre>
  //   return `<pre style="font-family: monospace; margin: 0; padding: 0; white-space: pre-wrap;">${JSON.stringify(JSON.parse(params.value), null, 2)}</pre>`;
  // }

  const resetRow = (orgRowObj) => {
    agGrid.gridApi.forEachNode((rowNode) => {
      if (orgRowObj.audienceTargetId === rowNode.data.audienceTargetId) {
        rowNode.setData(orgRowObj);
        setUpdatedRowCount(curentCount => curentCount - 1);
      }
    });
  };

  const removeRow = (rowData) => {
    agGrid.gridApi.applyTransaction({ remove: [rowData.data] });
    setUpdatedRowCount(curentCount => curentCount - 1);
  };

  const submitDspCT = () => {
    setSubmitting(true);
    const dspAudienceTargetsUrl = `${config.serverUrl}/management/dsp/targets/audience`;

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

    const saveData = [];
    // Build changes
    agGrid.gridApi.forEachNode((rowNode) => {
      if (rowNode.data.change || rowNode.data.audienceTargetId === 0) {
        const { change, ...rest } = rowNode.data;
        saveData.push({ ...rest }); // Remove fields we don't care about and add to changedRows
      }
    });

    const requestOptions = {
      method: 'POST',
      headers: headerWithAuth,
      body: JSON.stringify({
        accountId: selectedSeller.value,
        saveData,
      }),
    };

    fetch(dspAudienceTargetsUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then(() => {
        Notify.show('primary', 'Success', 'AudienceTargets have been saved!');
        setUpdatedRowCount(0);
        setForceRefresh(moment());
        setSubmitting(false);
      }).catch(() => {
        Notify.show('danger', 'Error', 'We were unable to save your AudienceTargets.');
        setForceRefresh(moment());
        setSubmitting(false);
      });
  };

  const highlightChanges = (row) => {
    if ((row.data?.change && row.value !== row.data.change[row.colDef.field]) || (row.data.audienceTargetId === 0)) {
      return { backgroundColor: '#91BE23' };
    }
    return { backgroundColor: '' };
  }

  const columnDefs = [
    {
      field: 'change',
      headerName: 'CHANGE',
      cellStyle: (params) => (params.value !== '' ? { color: '#91BE23' } : { color: 'black' }),
      cellRenderer: (params) => {
        if (params.value && params.data.audienceTargetId > 0) {
          return <button type="button" className="general_theme" style={{ height: '100%' }} onClick={() => resetRow(params.value)}>Reset Row</button>;
        }
        if (params.data.audienceTargetId === 0) {
          return <button type="button" className="general_theme" style={{ height: '100%' }} onClick={() => removeRow(params)}>Remove Row</button>;
        }
        return ''
      },
      editable: false,
    },
    { field: 'audienceTargetId', headerName: 'Audience Target ID', editable: false },
    { field: 'accountId', headerName: 'Account ID', editable: false },
    { field: 'adType', headerName: 'AD Type', cellStyle: params => highlightChanges(params) },
    { field: 'destinationPlatform', headerName: 'Destination Platform', cellStyle: params => highlightChanges(params) },
    { field: 'targetType', headerName: 'Target Type', cellStyle: params => highlightChanges(params) },
    { field: 'targetSourceItemId', headerName: 'Target Source Item ID', cellStyle: params => highlightChanges(params) },
    { field: 'itemSource', headerName: 'Item Source', cellStyle: params => highlightChanges(params) },
    { field: 'target', headerName: 'Target', cellStyle: params => highlightChanges(params) },
    { field: 'budgetGroup', headerName: 'Budget Group', cellStyle: params => highlightChanges(params) },
    { field: 'lpGroup', headerName: 'LP Group', cellStyle: params => highlightChanges(params) },
    { field: 'lpGroupType', headerName: 'LP Group Type', cellStyle: params => highlightChanges(params) },
    { field: 'subcategoryCsv', headerName: 'Subcategory CSV', cellStyle: params => highlightChanges(params) },
    { field: 'brandCsv', headerName: 'Brand CSV', cellStyle: params => highlightChanges(params) },
    { field: 'productTypeCsv', headerName: 'Product Type CSV', cellStyle: params => highlightChanges(params) },
    { field: 'targetIncludeTermsCsv', headerName: 'Target Include Terms CSV', cellStyle: params => highlightChanges(params) },
    { field: 'targetExculdeTermsCsv', headerName: 'Target Exculde Terms CSV', cellStyle: params => highlightChanges(params) },
    { field: 'createTs', headerName: 'Create TS', editable: false },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    editable: true,
  };

  const showDatePicker = false;
  const showExportOptions = true;
  const apiEndPoint = `${config.serverUrl}/management/dsp/targets/audience`;

  const exportPrefix = getExportPrefix('amz-dsp-targets-audience', selectedSeller, true);

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  }

  const parentSetData = (data) => {
    setOriginalData(JSON.parse(JSON.stringify(data)));
  }

  const onRowValueChanged = (cellData) => {
    if (!agGrid) {
      return;
    }
    agGrid.gridApi.forEachNode((rowNode) => {
      if (cellData.data.audienceTargetId === rowNode.data.audienceTargetId) {
        if (!rowNode.data.change) {
          const origData = originalData.find(origRow => origRow.audienceTargetId === cellData.data.audienceTargetId);
          rowNode.setDataValue('change', { ...origData });
          if (cellData.data.audienceTargetId !== 0) {
            setUpdatedRowCount(curentCount => curentCount + 1);
          }
        }
      }
    })
  }

  const getNewRow = () => {
    const newData = {
      audienceTargetId: 0,
      accountId: selectedSeller.value,
      adType: null,
      destinationPlatform: null,
      targetType: null,
      targetSourceItemId: null,
      itemSource: null,
      target: null,
      budgetGroup: null,
      lpGroup: null,
      lpGroupType: null,
      subcategoryCsv: null,
      brandCsv: null,
      productTypeCsv: null,
      targetIncludeTermsCsv: null,
      targetExculdeTermsCsv: null,
      createTs: null,
    };
    setUpdatedRowCount(curentCount => curentCount + 1);
    return newData;
  }


  useEffect(() => {
    if (agGrid) {
      agGrid.refreshData();
    }
  }, [forceRefresh]);

  return (
    <Container>
      <Row>
        <TableBuilder
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          apiEndPoint={apiEndPoint}
          showDatePicker={showDatePicker}
          showExportOptions={showExportOptions}
          exportPrefix={exportPrefix}
          parentAccess={parentAccess}
          parentSetData={parentSetData}
          onRowValueChanged={onRowValueChanged}
          showAddRowButton
          getNewRow={getNewRow}
        />
      </Row>
      <Row>
        <CardPanel
          title={`Updated Count: ${updatedRowCount}`}
          autorenewButton={false}
          closeButton={false}
        >
          <div style={{ width: '500px' }}>
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submitDspCT()}>Save Audience Targets</button>
          </div>
        </CardPanel>
      </Row>
    </Container>
  );
}
